import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useLang, SUPPORTED, LANG_NAMES } from "@/i18n/LanguageContext";

const VARIANTS = {
  dark: {
    btn: "border-white/25 bg-white/5 text-white hover:bg-white/10",
    menu: "bg-[#111110] border-white/15 text-white",
    item: "hover:bg-white/10",
  },
  light: {
    btn: "border-charcoal/30 bg-white/40 text-charcoal hover:bg-white/70",
    menu: "bg-paper border-charcoal/20 text-charcoal",
    item: "hover:bg-charcoal/5",
  },
  re: {
    btn: "border-paper/30 bg-ink/70 text-paper hover:bg-ink/90",
    menu: "bg-ink border-paper/20 text-paper",
    item: "hover:bg-paper/10",
  },
};

export default function LanguageSwitcher({ variant = "dark" }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const v = VARIANTS[variant] || VARIANTS.dark;

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        data-testid="language-switcher-button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 border backdrop-blur rounded-sm px-3 py-2 font-mono-data text-[0.66rem] tracking-[0.18em] transition-colors ${v.btn}`}
      >
        <Globe className="h-3.5 w-3.5 opacity-70" />
        {lang.toUpperCase()}
        <ChevronDown className={`h-3 w-3 opacity-60 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            data-testid="language-menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute right-0 mt-2 w-44 border rounded-sm shadow-2xl backdrop-blur-xl overflow-hidden z-[80] ${v.menu}`}
          >
            {SUPPORTED.map((code) => (
              <li key={code}>
                <button
                  data-testid={`language-option-${code}`}
                  onClick={() => { setLang(code); setOpen(false); }}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-[0.8rem] transition-colors ${v.item}`}
                >
                  <span>{LANG_NAMES[code]}</span>
                  <span className="flex items-center gap-2 font-mono-data text-[0.58rem] tracking-[0.15em] opacity-50">
                    {code.toUpperCase()}
                    {lang === code && <Check className="h-3 w-3 opacity-100 text-brass" />}
                  </span>
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
